/**
 * useStudioMic — everything that talks to the microphone.
 *
 * Responsibilities:
 *  - Ambient monitor: persistent mic → analyser (no destination) so the
 *    LevelMeter next to Record shows live room noise between takes.
 *  - Upfront noise-floor check (quiet/warn/blocked verdict) so Record can
 *    roll instantly without gating.
 *  - Recording: MediaRecorder + Analyser for live-peak painting, then
 *    decodes the blob, applies latency compensation, and punch-merges the
 *    take onto any existing vocal clip.
 *  - Latency calibration: click-round-trip measurement stored in localStorage.
 *
 * Owns mic-only refs (streams, analysers, buffers, recorder, chunks). Reads
 * shared refs (ctx, gains, sources, playing/recording, clips, position)
 * from `core` and calls the transport's `pause`/`tick` when appropriate.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CALIBRATION_HEADPHONE_BIAS_MS, LATENCY_NUDGE_STEP_MS,
  MAX_COMPENSATION_S,
  NOISE_BLOCK_DB, NOISE_CHECK_MS, NOISE_WARN_DB,
  clearCalibratedMs, effectiveLatencySeconds,
  mergePunch, readCalibratedSeconds,
  scheduleClip, stopSources, writeCalibratedMs,
} from './studioAudioUtils';

export function useStudioMic(core, transport) {
  const {
    ctxRef, gainsRef, sourcesRef, playStartRef, rafRef,
    playingRef, recordingRef, clipsRef,
    // mic refs are created here (owned by this hook) and mirrored back into
    // core so the transport's tick can read them without a cyclic import.
    analyserRef, analyserBufRef, liveVocalPeaksRef, recordStartPosRef,
    position, setIsPlaying, setIsRecording, setPosition,
    setSplitAt, setVocal, setInstrumental, pushUndo,
    getCtx, ensureCtxReady,
  } = core;
  const { pause, tick } = transport;

  const [micError, setMicError] = useState('');
  // Pre-record noise floor check: { status: 'idle'|'checking'|'warn'|'blocked'|'quiet'|'pending', db }
  const [noiseCheck, setNoiseCheck] = useState({ status: 'idle', db: null });

  // Recording bits
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // Persistent ambient monitor
  const ambientStreamRef = useRef(null);
  const ambientSrcRef = useRef(null);
  const ambientAnalyserRef = useRef(null);
  const ambientBufRef = useRef(null);
  const [ambientMonitorActive, setAmbientMonitorActive] = useState(false);

  // Latency calibration state
  const [latencyMs, setLatencyMs] = useState(() => {
    const cal = readCalibratedSeconds();
    return cal != null ? Math.round(cal * 1000) : null;
  });
  const [calibration, setCalibration] = useState({ status: 'idle', measuredMs: null, error: null });

  // ---- mic level -------------------------------------------------------
  const getMicLevel = useCallback(() => {
    // Returns instantaneous mic peak (0..1). Prefers the recording analyser
    // (live take) but falls back to the persistent ambient monitor so the
    // meter next to Record shows room noise between takes too.
    const analyser = analyserRef.current || ambientAnalyserRef.current;
    const buf = analyserRef.current ? analyserBufRef.current : ambientBufRef.current;
    if (!analyser || !buf) return 0;
    analyser.getByteTimeDomainData(buf);
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = Math.abs(buf[i] - 128) / 128;
      if (v > peak) peak = v;
    }
    return peak;
  }, [analyserRef, analyserBufRef]);

  // ---- live dB (float RMS from the active analyser — recording or ambient)
  const floatDbBufRef = useRef(null);
  const getMicDb = useCallback(() => {
    const analyser = analyserRef.current || ambientAnalyserRef.current;
    if (!analyser || typeof analyser.getFloatTimeDomainData !== 'function') return null;
    let buf = floatDbBufRef.current;
    if (!buf || buf.length !== analyser.fftSize) {
      buf = new Float32Array(analyser.fftSize);
      floatDbBufRef.current = buf;
    }
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    return 20 * Math.log10(Math.max(rms, 1e-6));
  }, [analyserRef]);

  // ---- noise-floor measurement ---------------------------------------
  // Reads the mic in 32-bit float (getFloatTimeDomainData) — the 8-bit byte
  // path only resolves ~256 levels, which collapses every quiet room to the
  // same reading below ~-48 dBFS. Float gives true resolution down to the
  // hardware noise floor so different environments measure differently.
  const measureNoiseFloor = useCallback(async (analyser) => {
    const hasFloat = typeof analyser.getFloatTimeDomainData === 'function';
    const floatBuf = hasFloat ? new Float32Array(analyser.fftSize) : null;
    const byteBuf = hasFloat ? null : new Uint8Array(analyser.fftSize);
    const readDb = () => {
      let sum = 0;
      if (hasFloat) {
        analyser.getFloatTimeDomainData(floatBuf);
        for (let i = 0; i < floatBuf.length; i++) sum += floatBuf[i] * floatBuf[i];
      } else {
        analyser.getByteTimeDomainData(byteBuf);
        for (let i = 0; i < byteBuf.length; i++) {
          const v = (byteBuf[i] - 128) / 128;
          sum += v * v;
        }
      }
      const rms = Math.sqrt(sum / analyser.fftSize);
      return 20 * Math.log10(Math.max(rms, 1e-6)); // floor at -120 dB
    };
    setNoiseCheck({ status: 'checking', db: null });
    const samples = [];
    const intervalMs = 100;
    const count = Math.round(NOISE_CHECK_MS / intervalMs);
    const aborted = await new Promise((resolve) => {
      const iv = setInterval(() => {
        if (recordingRef.current) { clearInterval(iv); resolve(true); return; }
        const db = readDb();
        samples.push(db);
        setNoiseCheck({ status: 'checking', db });
        if (samples.length >= count) { clearInterval(iv); resolve(false); }
      }, intervalMs);
    });
    if (aborted) return null;
    samples.sort((a, b) => a - b);
    return samples[Math.floor(samples.length / 2)]; // median — robust to brief spikes
  }, [recordingRef]);

  // ---- ambient monitor (persistent mic → analyser, no destination) ---
  const checkingRef = useRef(false);

  const stopAmbientMonitor = useCallback(() => {
    try { ambientSrcRef.current?.disconnect(); } catch (e) { /* noop */ }
    try { ambientAnalyserRef.current?.disconnect(); } catch (e) { /* noop */ }
    ambientStreamRef.current?.getTracks().forEach((t) => t.stop());
    ambientStreamRef.current = null;
    ambientSrcRef.current = null;
    ambientAnalyserRef.current = null;
    ambientBufRef.current = null;
    setAmbientMonitorActive(false);
  }, []);

  const openAmbientMonitor = useCallback(async () => {
    if (ambientStreamRef.current || recordingRef.current || checkingRef.current) return;
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = getCtx();
      if (ctx.state !== 'running') { stream.getTracks().forEach((t) => t.stop()); return; }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      const srcNode = ctx.createMediaStreamSource(stream);
      srcNode.connect(analyser);           // analyser only — NOT connected to destination (no monitoring/feedback)
      ambientStreamRef.current = stream;
      ambientSrcRef.current = srcNode;
      ambientAnalyserRef.current = analyser;
      ambientBufRef.current = new Uint8Array(analyser.fftSize);
      setAmbientMonitorActive(true);
    } catch (e) {
      stream?.getTracks().forEach((t) => t.stop());
    }
  }, [getCtx, recordingRef]);

  const runNoiseCheck = useCallback(async () => {
    if (checkingRef.current || recordingRef.current) return;
    checkingRef.current = true;
    setMicError('');
    // Release any existing ambient monitor before opening a fresh mic so
    // devices with a single input can grant access without contention.
    stopAmbientMonitor();
    let stream = null;
    let srcNode = null;
    let analyser = null;
    let buf = null;
    let promoted = false;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
      } catch (e) {
        setMicError('Microphone access denied. Allow mic permission in your browser to record.');
        setNoiseCheck({ status: 'idle', db: null });
        return;
      }
      const ctx = getCtx();
      if (ctx.state !== 'running') {
        // NOTE: resume() without a user gesture never settles on Chrome —
        // race it against a short timeout instead of awaiting it directly.
        try {
          await Promise.race([ctx.resume(), new Promise((res) => setTimeout(res, 400))]);
        } catch (e) { /* gesture required */ }
      }
      if (ctx.state !== 'running') {
        // iOS Safari won't run an AudioContext before a user gesture —
        // defer: the 'pending' status re-triggers this check on first tap.
        setNoiseCheck({ status: 'pending', db: null });
        return;
      }
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      srcNode = ctx.createMediaStreamSource(stream);
      srcNode.connect(analyser);
      buf = new Uint8Array(analyser.fftSize);
      const db = await measureNoiseFloor(analyser);
      if (db == null) { setNoiseCheck({ status: 'idle', db: null }); return; } // aborted by a recording
      const status = db >= NOISE_BLOCK_DB ? 'blocked' : db >= NOISE_WARN_DB ? 'warn' : 'quiet';
      setNoiseCheck({ status, db });
      // Promote this mic stream + analyser to the persistent ambient monitor
      // so the level meter next to Record shows live room noise between takes.
      ambientStreamRef.current = stream;
      ambientSrcRef.current = srcNode;
      ambientAnalyserRef.current = analyser;
      ambientBufRef.current = buf;
      setAmbientMonitorActive(true);
      promoted = true;
    } finally {
      if (!promoted) {
        try { srcNode?.disconnect(); } catch (e) { /* noop */ }
        try { analyser?.disconnect(); } catch (e) { /* noop */ }
        stream?.getTracks().forEach((t) => t.stop());
      }
      checkingRef.current = false;
    }
  }, [getCtx, measureNoiseFloor, stopAmbientMonitor, recordingRef]);

  // 'pending' → run the deferred check on the first user gesture (iOS Safari).
  useEffect(() => {
    if (noiseCheck.status !== 'pending') return;
    const handler = () => runNoiseCheck();
    window.addEventListener('pointerdown', handler, { once: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, [noiseCheck.status, runNoiseCheck]);

  const releaseMic = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { analyserRef.current?.disconnect(); } catch (e) { /* noop */ }
    analyserRef.current = null;
    analyserBufRef.current = null;
  }, [analyserRef, analyserBufRef]);

  // Mirror of the latest verdict so startRecording (a stable callback) can
  // refuse to roll while the room is blocked or a check is mid-flight.
  const noiseStatusRef = useRef('idle');
  noiseStatusRef.current = noiseCheck.status;

  // Recording starts INSTANTLY — the room was already checked when the
  // Studio opened (runNoiseCheck), so there is no gate here.
  const startRecording = useCallback(async () => {
    if (noiseStatusRef.current === 'blocked' || noiseStatusRef.current === 'checking') return false;
    setMicError('');
    setSplitAt(null);
    // Release the ambient monitor's mic — the recording flow will open its
    // own dedicated stream and drive the same level meter.
    stopAmbientMonitor();
    const ctx = await ensureCtxReady();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
    } catch (e) {
      setMicError('Microphone access denied. Allow mic permission in your browser to record.');
      return false;
    }
    streamRef.current = stream;
    chunksRef.current = [];
    // Wire the mic through an AnalyserNode so we can paint live peaks.
    // NOTE: analyser is NOT connected to ctx.destination — no monitoring/feedback.
    try {
      const srcNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      srcNode.connect(analyser);
      analyserRef.current = analyser;
      analyserBufRef.current = new Uint8Array(analyser.fftSize);
    } catch (e) {
      // if AnalyserNode setup fails we still record — waveform just won't animate
      analyserRef.current = null;
      analyserBufRef.current = null;
    }

    liveVocalPeaksRef.current = [];
    const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
      : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorderRef.current = recorder;

    const startPos = position;
    recordStartPosRef.current = startPos;
    recorder.start(250);
    recordingRef.current = true;
    setIsRecording(true);
    // roll the instrumental under the take (vocal channel is replaced by the new take)
    stopSources(sourcesRef);
    const when = ctx.currentTime + 0.08;
    scheduleClip(ctx, clipsRef.current.instrumental, gainsRef.current.inst, startPos, when, sourcesRef);
    playStartRef.current = { ctxTime: when, pos: startPos };
    playingRef.current = true;
    setIsPlaying(true);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return true;
  }, [
    ensureCtxReady, position, stopAmbientMonitor, tick,
    setSplitAt, setIsPlaying, setIsRecording,
    analyserRef, analyserBufRef, liveVocalPeaksRef, recordStartPosRef,
    recordingRef, playingRef, playStartRef, sourcesRef, gainsRef, clipsRef, rafRef,
  ]);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recordingRef.current = false;
    setIsRecording(false);
    pause();

    const blob = await new Promise((resolve) => {
      recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' }));
      recorder.stop();
    });
    releaseMic();

    try {
      const ctx = getCtx();
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      // Compensate for full roundtrip latency (speaker output + mic input) so
      // the vocal lines up with what the artist actually heard. Uses their
      // calibrated value if present, else a per-platform estimate.
      const latency = effectiveLatencySeconds(ctx);
      const offset = Math.max(0, recordStartPosRef.current - Math.min(latency, MAX_COMPENSATION_S));
      const prev = clipsRef.current.vocal;
      // Punch recording: merge the new take over the existing one — keep
      // everything outside the newly recorded region, replace what's under it.
      pushUndo();
      setVocal(prev ? mergePunch(ctx, prev, { buffer, offset }) : { buffer, offset });
      setPosition(recordStartPosRef.current);
      liveVocalPeaksRef.current = [];
    } catch (e) {
      setMicError('Could not process the recording. Please try again.');
    }
    // Bring the ambient level meter back to life between takes — fire and
    // forget so the take-finalize path isn't blocked on getUserMedia.
    openAmbientMonitor();
  }, [
    getCtx, openAmbientMonitor, pause, pushUndo, releaseMic,
    setIsRecording, setPosition, setVocal,
    recordingRef, clipsRef, recordStartPosRef, liveVocalPeaksRef,
  ]);

  // ---- latency calibration ---------------------------------------------
  // Plays a short click through the speaker while recording the mic, then
  // measures how long the click took to travel through
  // schedule → speaker → air → mic. That IS the roundtrip latency we
  // subtract in stopRecording so vocals line up with the beat.
  const calibrateLatency = useCallback(async () => {
    setCalibration({ status: 'preparing', measuredMs: null, error: null });
    let stream = null;
    let src = null;
    let proc = null;
    let silent = null;
    try {
      const ctx = await ensureCtxReady();
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const rate = ctx.sampleRate;
      const BUFFER = 1024;
      // ScriptProcessorNode is deprecated but works on iOS Safari and gives
      // us sample-accurate access to the mic stream in AudioContext time.
      src = ctx.createMediaStreamSource(stream);
      proc = ctx.createScriptProcessor(BUFFER, 1, 1);
      silent = ctx.createGain();
      silent.gain.value = 0;

      const captured = [];
      let firstChunkPlaybackTime = null;
      proc.onaudioprocess = (e) => {
        if (firstChunkPlaybackTime === null) firstChunkPlaybackTime = e.playbackTime || ctx.currentTime;
        captured.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      src.connect(proc);
      proc.connect(silent);
      silent.connect(ctx.destination);

      // Warm-up: let the mic start flowing and any auto-gain settle
      setCalibration({ status: 'recording', measuredMs: null, error: null });
      await new Promise((r) => setTimeout(r, 350));

      // Build a short high-frequency click (2 kHz, ~10 ms, exp decay)
      const clickLen = Math.round(rate * 0.010);
      const clickBuf = ctx.createBuffer(1, clickLen, rate);
      const cData = clickBuf.getChannelData(0);
      for (let i = 0; i < clickLen; i++) {
        const env = Math.exp(-i / (rate * 0.0022));
        cData[i] = Math.sin(2 * Math.PI * 2000 * i / rate) * env;
      }
      const clickSrc = ctx.createBufferSource();
      clickSrc.buffer = clickBuf;
      // A little extra gain so it's audible over environment
      const clickGain = ctx.createGain();
      clickGain.gain.value = 0.9;
      clickSrc.connect(clickGain);
      clickGain.connect(ctx.destination);
      const scheduleAt = ctx.currentTime + 0.30;
      clickSrc.start(scheduleAt);

      // Wait long enough to capture the click + a comfortable roundtrip window
      await new Promise((r) => setTimeout(r, 700));

      // Tear down capture
      try { proc.disconnect(); } catch (e) { /* noop */ }
      try { silent.disconnect(); } catch (e) { /* noop */ }
      try { src.disconnect(); } catch (e) { /* noop */ }
      stream.getTracks().forEach((t) => t.stop());
      stream = null;

      if (firstChunkPlaybackTime === null || captured.length === 0) {
        throw new Error('No audio captured');
      }

      // Assemble the captured stream
      let total = 0;
      for (const c of captured) total += c.length;
      const samples = new Float32Array(total);
      let off = 0;
      for (const c of captured) { samples.set(c, off); off += c.length; }

      // playbackTime is when the output block will play — the same block just
      // carried our INPUT samples so its capture time is playbackTime - block.
      const sample0CtxTime = firstChunkPlaybackTime - BUFFER / rate;
      const scheduleAtIdx = Math.round((scheduleAt - sample0CtxTime) * rate);
      const searchStart = Math.max(0, scheduleAtIdx - Math.round(0.03 * rate));

      // Compute noise floor in the pre-click window to set an adaptive threshold
      let noise = 0;
      const noiseEnd = Math.max(0, searchStart - Math.round(0.02 * rate));
      const noiseStart = Math.max(0, noiseEnd - Math.round(0.15 * rate));
      for (let i = noiseStart; i < noiseEnd; i++) {
        const v = Math.abs(samples[i]);
        if (v > noise) noise = v;
      }
      const threshold = Math.max(noise * 4, 0.03);

      let firstIdx = -1;
      for (let i = searchStart; i < samples.length; i++) {
        if (Math.abs(samples[i]) > threshold) { firstIdx = i; break; }
      }
      if (firstIdx < 0) {
        throw new Error('Click not detected. Increase device volume, remove headphones (or point their mic at the speaker), and try again.');
      }

      const actualClickCtxTime = sample0CtxTime + firstIdx / rate;
      const roundtrip = actualClickCtxTime - scheduleAt;
      if (!Number.isFinite(roundtrip) || roundtrip < 0.005 || roundtrip > MAX_COMPENSATION_S) {
        throw new Error(`Measured latency out of range (${Math.round(roundtrip * 1000)} ms). Please retry.`);
      }
      const ms = Math.round(roundtrip * 1000);
      // Apply the headphone bias so the stored compensation matches what the
      // artist hears in-ear during a real take (see studioAudioUtils.js).
      const biased = Math.min(
        Math.round(MAX_COMPENSATION_S * 1000),
        Math.max(0, ms + CALIBRATION_HEADPHONE_BIAS_MS),
      );
      writeCalibratedMs(biased);
      setLatencyMs(biased);
      setCalibration({ status: 'done', measuredMs: biased, error: null });
      return biased;
    } catch (err) {
      // Best-effort cleanup on any failure path
      try { proc?.disconnect(); } catch (e) { /* noop */ }
      try { silent?.disconnect(); } catch (e) { /* noop */ }
      try { src?.disconnect(); } catch (e) { /* noop */ }
      stream?.getTracks().forEach((t) => t.stop());
      const msg = err?.message || 'Calibration failed';
      setCalibration({ status: 'error', measuredMs: null, error: msg });
      throw err;
    }
  }, [ensureCtxReady]);

  const resetCalibration = useCallback(() => {
    clearCalibratedMs();
    setLatencyMs(null);
    setCalibration({ status: 'idle', measuredMs: null, error: null });
  }, []);

  const dismissCalibration = useCallback(() => {
    setCalibration({ status: 'idle', measuredMs: null, error: null });
  }, []);

  // Manual fine-tune: nudge the stored latency by ±LATENCY_NUDGE_STEP_MS so
  // the artist can dial in the timing after calibration if it still feels off
  // (e.g. different headphones on the same session). Clamped to [0, MAX].
  // If a vocal take already exists on the timeline, it slides to mirror the
  // change (larger latency ⇒ vocal offset shifts earlier by the same amount)
  // so the recorded take stays aligned with what the artist heard — they can
  // punch back in from that spot without re-recording the whole thing.
  //
  // When the vocal has no room to move earlier (would go below 0), the
  // instrumental is pushed to the right by the overshoot instead — this
  // preserves the vocal-to-riddim relative timing so the artist can keep
  // going up to +N ms of extra latency even after they started recording
  // right at 0:00.
  const nudgeLatency = useCallback((deltaMs) => {
    const base = latencyMs != null ? latencyMs : 0;
    const next = Math.min(
      Math.round(MAX_COMPENSATION_S * 1000),
      Math.max(0, base + deltaMs),
    );
    const applied = next - base;
    if (applied === 0) return;
    writeCalibratedMs(next);
    setLatencyMs(next);
    const voc = clipsRef.current.vocal;
    if (!voc) return;
    const shiftSec = applied / 1000; // positive shift ⇒ vocal moves earlier
    const rawNewVocOffset = voc.offset - shiftSec;
    const inst = clipsRef.current.instrumental;
    if (rawNewVocOffset < 0 && inst) {
      // No timeline room for the vocal — hold it at 0 and push the riddim
      // right by the overshoot so relative alignment is preserved.
      const overshoot = -rawNewVocOffset;
      setInstrumental({ ...inst, offset: inst.offset + overshoot });
      setVocal({ ...voc, offset: 0 });
    } else if (rawNewVocOffset !== voc.offset) {
      setVocal({ ...voc, offset: Math.max(0, rawNewVocOffset) });
    }
  }, [latencyMs, clipsRef, setVocal, setInstrumental]);

  // ---- cleanup: release mic streams on unmount so the browser drops the
  // recording indicator when the artist leaves the Studio.
  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    ambientStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  return {
    micError, noiseCheck, runNoiseCheck,
    ambientMonitorActive, getMicLevel, getMicDb,
    startRecording, stopRecording,
    latencyMs, calibration, calibrateLatency, resetCalibration, dismissCalibration, nudgeLatency,
  };
}
